import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Toggle from 'react-toggled';
import {
  CreditClaimDeletedFilter,
  CreditClaimDeletedFilterValues,
  CreditClaimStatus,
  CreditClaimStatusValues,
  creditClaimStatusOptions,
  StatusCheckbox,
} from '../../constants/ApplicationStatus';
import { fetchClaims } from '../../ducks/claims';
import { PageSection, PageSectionTitle } from '../../layouts/PageStructure';
import { claimLoadingSelector } from '../../selectors/claims';
import { isAdmin as isAdminSelector, isCreditProcessor } from '../../selectors/user';
import styled from '../../theme';
import { Color } from '../../theme/primaryTheme';
import { ExternalCreditEarner } from '../../types';
import ApplicationTable from '../ApplicationTable';
import { Dropdown, DropdownButton, DropdownPanel } from '../Dropdown';
import CheckBox from '../Field/Controls/CheckBox';
import { Grid } from '../Grid';
import Text, { TextStyle } from '../Text';

interface Props {
  user: ExternalCreditEarner;
}

const UserApplications: React.FunctionComponent<Props> = ({ user }) => {
  const dispatch = useDispatch();
  const isProcessor = useSelector(isCreditProcessor);
  const isAdmin = useSelector(isAdminSelector);
  const isLoading = useSelector(claimLoadingSelector);

  const [statusFilters, setStatusFilters] = React.useState<CreditClaimStatus[]>(CreditClaimStatusValues);
  const [deletedFilter, setDeletedFilter] = React.useState<CreditClaimDeletedFilter>(CreditClaimDeletedFilter.ONLY_NON_DELETED);
  const savedStatusFilters = React.useRef<CreditClaimStatus[] | null>(null);

  const handleStatusFilter = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = evt.currentTarget;
    const selectedCreditClaimStatus = name as CreditClaimStatus;
    const index = statusFilters.indexOf(selectedCreditClaimStatus);
    const newStatusFilters = [...statusFilters];

    if (index > -1) {
      newStatusFilters.splice(index, 1);
    } else {
      newStatusFilters.push(selectedCreditClaimStatus);
    }

    setStatusFilters(newStatusFilters);
    dispatch(fetchClaims({ userId: user.id, status: newStatusFilters, page: 1, withDeleted: deletedFilter }));
  };

  const handleDeletionFilter = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = evt.currentTarget;
    const newDeletionFilter = name as CreditClaimDeletedFilter;

    let newStatusFilters: CreditClaimStatus[];
    if (newDeletionFilter === CreditClaimDeletedFilter.ONLY_DELETED) {
      savedStatusFilters.current = statusFilters;
      newStatusFilters = CreditClaimStatusValues;
    } else if (deletedFilter === CreditClaimDeletedFilter.ONLY_DELETED) {
      newStatusFilters = savedStatusFilters.current ?? statusFilters;
      savedStatusFilters.current = null;
    } else {
      newStatusFilters = statusFilters;
    }

    setDeletedFilter(newDeletionFilter);
    setStatusFilters(newStatusFilters);
    dispatch(fetchClaims({ userId: user.id, status: newStatusFilters, page: 1, withDeleted: newDeletionFilter }));
  };

  return (
    <PageSection>
      <PageSectionTitle>
        <Text tag="h3" bold={true} textStyle={TextStyle.SectionTitle}>
          Applications
        </Text>
        {(isProcessor || isAdmin) && (
          <div>
            <Grid gridTemplateColumns="auto auto">
              <Toggle>
                {({ on, toggle, setOff, getTogglerProps }) => (
                  <Dropdown>
                    <DropdownButton
                      isOpen={on}
                      onClick={toggle}
                      removePadding={true}
                      colorKey={Color.Label}
                      data-test-id="openDeletedFilter"
                      {...getTogglerProps()}
                    >
                      Filter By Deletion
                    </DropdownButton>
                    {on && (
                      <DropdownPanel arrow={true} onClickOutside={setOff} isOpen={on} width={250}>
                        <ApplicationStatusFilters data-test-id="deletedFilterMenu">
                          {CreditClaimDeletedFilterValues.map((filter) => (
                            <CheckBox
                              key={filter}
                              name={filter}
                              title={filter}
                              checked={deletedFilter === filter}
                              disabled={isLoading}
                              textProps={{
                                bold: false,
                                textStyle: TextStyle.Small,
                              }}
                              onChange={handleDeletionFilter}
                            />
                          ))}
                        </ApplicationStatusFilters>
                      </DropdownPanel>
                    )}
                  </Dropdown>
                )}
              </Toggle>
              <Toggle>
                {({ on, toggle, setOff, getTogglerProps }) => (
                  <Dropdown>
                    <DropdownButton
                      isOpen={on}
                      onClick={toggle}
                      removePadding={true}
                      colorKey={Color.Label}
                      data-test-id="openStatusFilter"
                      {...getTogglerProps()}
                    >
                      Filter By Status
                    </DropdownButton>
                    {on && (
                      <DropdownPanel arrow={true} onClickOutside={setOff} isOpen={on} width={250}>
                        <ApplicationStatusFilters data-test-id="statusFilterMenu">
                          {creditClaimStatusOptions.map((status: StatusCheckbox) => (
                            <CheckBox
                              key={status.value}
                              name={status.value}
                              title={status.label}
                              checked={statusFilters.includes(status.value)}
                              disabled={isLoading}
                              textProps={{
                                bold: false,
                                textStyle: TextStyle.Small,
                              }}
                              onChange={handleStatusFilter}
                            />
                          ))}
                        </ApplicationStatusFilters>
                      </DropdownPanel>
                    )}
                  </Dropdown>
                )}
              </Toggle>
            </Grid>
          </div>
        )}
      </PageSectionTitle>
      <ApplicationTable userId={user.id} statusFilters={statusFilters} deletedFilter={deletedFilter} />
    </PageSection>
  );
};

const ApplicationStatusFilters = styled.div`
  padding: 2rem;

  > div + div {
    margin-top: 0.25em;
  }
`;

export default UserApplications;
